/**
 * Which model-gated nav entries this node should show.
 *
 * The sidebar's per-model report entries are only useful when the model behind them is
 * actually running on a camera, so the nav asks this hook and drops the rest (see
 * NavNode.requiresModel).
 *
 * Fails OPEN: while the registry is loading, and if the request fails or is denied
 * (`models.view` / `ai-models.view` is not in every role), every gated entry stays visible.
 * A transient 502 or a role without registry access must not silently amputate the nav —
 * hiding a report the operator uses daily is far worse than showing one that turns out empty.
 */
import { useEffect, useMemo, useState } from 'react';
import { useApi, type Model } from '../api';
import { activeModelSurfaces } from '../constants/modelSurfaces';

/** Enough to cover any real registry in one request; the page-level list uses the same bound. */
const FETCH_LIMIT = 500;

/**
 * Registries already read this session, by node.
 *
 * The model registry is the SLOW HALF of a dependency, not a page's own data: the dashboard
 * can't ask the summary endpoint anything until it knows which surfaces this node runs, so
 * every visit paid a round trip to the backend before the analytics request could even start —
 * for a list that changes when somebody installs a model, which is roughly never.
 *
 * A cache hit paints from memory and then revalidates in the background, so a registry that
 * DID change is picked up on the same visit rather than at the next reload. Module-level and
 * deliberately not persisted: one session is exactly the window where "nobody installed a
 * model since" is safe to assume, and sessionStorage would carry a stale list across a deploy.
 */
const REGISTRY_CACHE = new Map<string, Model[]>();

export interface ActiveModelSurfaces {
  /** True when `surface` is unlocked — and true for everything until the registry is known. */
  hasModel: (surface: string) => boolean;
  /** False until the registry has been read at least once (or the read failed). */
  loaded: boolean;
  /**
   * The active surfaces as a concrete list, or `null` while the registry is unknown (still
   * loading, or the read failed/was denied).
   *
   * `hasModel` fails OPEN because hiding a nav entry an operator uses daily is worse than
   * showing an empty one. A caller that needs to ENUMERATE the surfaces — the dashboard, which
   * asks the summary endpoint for exactly the models this node runs — cannot use that: an
   * open-failed predicate would have it request every surface and draw a card for models
   * nobody runs, which is the opposite of what dynamic cards mean. So this is null rather
   * than "everything", and the caller decides what to do about not knowing.
   */
  surfaces: string[] | null;
}

/**
 * @param nodeKey Cloud: the selected node id. Changing it refetches, since the registry is
 *                per-node. Local: omit.
 */
export function useActiveModelSurfaces(nodeKey?: string | null): ActiveModelSurfaces {
  const { models: modelsApi } = useApi();
  const [models, setModels] = useState<Model[] | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const key = nodeKey || '__default__';
    const cached = REGISTRY_CACHE.get(key);

    // Paint from the cache first — this is a dependency other requests wait behind, so the
    // difference between "instant" and "one round trip" is the difference between the
    // dashboard's model cards arriving with the page and arriving after it.
    setModels(cached ?? null);
    setLoaded(!!cached);

    modelsApi
      .list({ page: 1, limit: FETCH_LIMIT })
      .then((res) => {
        const rows = Array.isArray(res?.data) ? res.data : [];
        REGISTRY_CACHE.set(key, rows);
        if (cancelled) return;
        setModels(rows);
        setLoaded(true);
      })
      .catch(() => {
        // Leave whatever the cache gave us. With no cache `models` stays null and hasModel()
        // keeps every nav entry visible — failing open, as the header says.
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeKey]);

  const surfaces = useMemo(() => (models ? activeModelSurfaces(models) : null), [models]);

  return useMemo(
    () => ({
      hasModel: (surface: string) => (surfaces ? surfaces.has(surface) : true),
      loaded,
      surfaces: surfaces ? [...surfaces].sort() : null,
    }),
    [surfaces, loaded],
  );
}
